import { useMemo } from 'react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import {
  useInsightsComparison,
  useInsightsDateRange,
  useInsightsTags,
} from '../context/InsightsPageContext'

/** Location and identity of a single click within the Insights experience. */
export type InsightsItemClickedProperties = {
  /** Durable slug for the Insights page the click happened on (e.g. `channel-insights`). */
  page: string
  /** Durable slug for the section within the page (e.g. `metrics`). */
  section: string
  /** Durable identifier for the control clicked (e.g. `view-tab`, `metric-dropdown`). */
  element: string
  /** The option selected, where the element offers a choice — durable internal key, not a label. */
  item?: string | null
  /** Id of a more specific object the action targets (e.g. a post id). */
  targetId?: string | null
  /** Kind of object `targetId` refers to (e.g. `post`). */
  targetType?: string | null
  /** Channel(s) in scope when the click happened. */
  channelIds?: string[] | null
  /** Services of the channels in scope, aligned with `channelIds`. */
  serviceTypes?: string[] | null
}

type TrackInsightsItemClicked = (
  properties: InsightsItemClickedProperties,
) => void

/**
 * Returns an `Insights Item Clicked` tracker for any clickable element across the
 * Insights experience. Callers pass the click's location/identity (page, section,
 * element, optional item/target + channel context); the active filter state — date
 * range, comparison mode, and tag filter — is read from the Insights page context
 * so every click is annotated with the view the user was looking at.
 *
 * Mirrors {@link useTrackTakeawayFeedbackClicked}: fires `BufferTracker` directly
 * with the common tracking props. Must be called within `InsightsPageProvider`.
 */
export function useTrackInsightsItemClicked(): TrackInsightsItemClicked {
  const organizationId = useOrganizationId()
  const commonTrackingProperties = useCommonTrackingProperties()
  const { selectedDateRange } = useInsightsDateRange()
  const { comparisonMode } = useInsightsComparison()
  const { selectedTagIds } = useInsightsTags()

  return useMemo(
    () =>
      ({
        page,
        section,
        element,
        item,
        targetId,
        targetType,
        channelIds,
        serviceTypes,
      }: InsightsItemClickedProperties): void => {
        if (!organizationId) return
        BufferTracker.insightsItemClicked({
          ...commonTrackingProperties,
          clientName: Client.PublishWeb,
          product: Product.Publish,
          organizationId,
          page,
          section,
          element,
          item: item ?? null,
          targetId: targetId ?? null,
          targetType: targetType ?? null,
          channelIds: channelIds ?? null,
          serviceTypes: serviceTypes ?? null,
          dateRange: selectedDateRange ?? null,
          comparisonMode: comparisonMode ?? null,
          tagIds: selectedTagIds.length > 0 ? selectedTagIds : null,
        })
      },
    [
      organizationId,
      commonTrackingProperties,
      selectedDateRange,
      comparisonMode,
      selectedTagIds,
    ],
  )
}
